import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "space-y-6 sm:space-y-8" }
const _hoisted_2 = { class: "card" }
const _hoisted_3 = { class: "card-body" }
const _hoisted_4 = { class: "flex flex-col sm:flex-row items-center sm:items-start space-y-5 sm:space-y-0 sm:space-x-8" }
const _hoisted_5 = { class: "h-20 w-20 sm:h-24 sm:w-24 rounded-full bg-gradient-to-br from-primary to-primary-hover dark:from-primary-dark dark:to-primary-dark-hover flex items-center justify-center text-white text-3xl sm:text-4xl font-bold shadow-elegant-lg transform transition-transform duration-300 hover:scale-105 ring-4 ring-primary/10 dark:ring-primary-dark/20" }
const _hoisted_6 = { class: "text-center sm:text-left flex-1" }
const _hoisted_7 = { class: "text-2xl sm:text-3xl font-bold text-primary-text dark:text-primary-dark-text" }
const _hoisted_8 = { class: "text-sm sm:text-base text-secondary-text/70 dark:text-secondary-dark-text/70 mt-2" }
const _hoisted_9 = { class: "grid grid-cols-1 gap-5 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3" }
const _hoisted_10 = { class: "grid grid-cols-1 gap-5 sm:gap-6 md:grid-cols-3" }
const _hoisted_11 = { class: "card" }
const _hoisted_12 = { class: "card-body" }
const _hoisted_13 = {
  key: 0,
  class: "flex justify-center py-10"
}
const _hoisted_14 = {
  key: 1,
  class: "w-full",
  style: {"height":"300px"}
}
const _hoisted_15 = { class: "card" }
const _hoisted_16 = { class: "card-body" }

import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Bar } from 'vue-chartjs'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js'
import { useTitle } from '@/composables/ui/useTitle'
import { useUser } from '@/composables/auth/useUser'
import { useAuthStore } from '@/stores/auth'
import { completedFormService, type CompletedForm } from '@/services/completedFormService'
import { materialService } from '@/services/material.service'
import DashboardStatsCard from '@/components/dashboard/DashboardStatsCard.vue'
import QuickActionButton from '@/components/dashboard/QuickActionButton.vue'
import ActivityTimeline from '@/components/dashboard/ActivityTimeline.vue'
import { Loader2, Users, FileText, File, Clock } from 'lucide-vue-next'


export default /*@__PURE__*/_defineComponent({
  __name: 'AdminDashboardView',
  setup(__props) {

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const appTitle = computed(() => process.env.VUE_APP_TITLE || 'PLANET Home Healthcare')
useTitle(`${appTitle.value} - Dashboard`)

const router = useRouter()
const authStore = useAuthStore()
const { currentUser } = useUser()

const userFullName = computed (() => {
	const firstname = currentUser.value?.first_name || ''
	const lastname = currentUser.value?.last_name || ''

	return firstname && lastname ? `${firstname} ${lastname}` : (currentUser.value?.email || 'User')
})

const isAdmin = computed(() => authStore.user?.role === 'admin')

const userInitials = computed(() => {
	const name = currentUser.value?.name || currentUser.value?.first_name || ''
	
	return name
		.split(' ')
		.map(word => word[0])
		.join('')
		.toUpperCase()
		.slice(0, 2)
})

const materials = ref<any[]>([])
const completedForms = ref<CompletedForm[]>([])
const loading = ref(false)
const chartLoading = ref(false)
const totalEmployees = ref(0)

const assignedMaterials = computed(() => {
  if (isAdmin.value) return materials.value
  const uid = currentUser.value?.id
  const pid = currentUser.value?.position_id
  if (!uid) return []
  return materials.value.filter((m: any) => {
    const posMatch = m.position_viewers?.some((p: any) => p.id === pid)
    const userMatch = m.user_viewers?.some((u: any) => u.id === uid)
    const noViewers = (!m.position_viewers || m.position_viewers.length === 0) &&
                      (!m.user_viewers || m.user_viewers.length === 0)
    return posMatch || userMatch || noViewers
  })
})

const pendingForms = computed(() => {
  if (isAdmin.value) return 0
  const uid = currentUser.value?.id
  if (!uid) return 0
  const completedIds = new Set(
    completedForms.value
      .filter(f => Number(f.user_id) === uid && f.fk_material_id)
      .map(f => Number(f.fk_material_id))
  )
  return assignedMaterials.value.filter(
    (m: any) => m.type === 'Online form' && !completedIds.has(m.id)
  ).length
})

const chartData = computed(() => {
	const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
	const now = new Date()
	const last6 = Array.from({ length: 6 }, (_, i) => {
		const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
		return { label: months[d.getMonth()], year: d.getFullYear(), month: d.getMonth() }
	}).reverse()

	const formsByMonth = last6.map(m => {
		return completedForms.value.filter(f => {
			const d = toDate(f.date_sent || f.created_at)
			return d.getMonth() === m.month && d.getFullYear() === m.year
		}).length
	})

	const materialsByMonth = last6.map(m => {
		return assignedMaterials.value.filter((mat: any) => {
			const d = new Date(mat.created_at)
			return d.getMonth() === m.month && d.getFullYear() === m.year
		}).length
	})

	return {
		labels: last6.map(m => m.label),
		datasets: [
			{
				label: 'Completed Forms',
				backgroundColor: '#10b981',
				data: formsByMonth,
			},
			{
				label: 'Materials',
				backgroundColor: '#f59e0b',
				data: materialsByMonth,
			},
		],
	}
})

const chartOptions = {
	responsive: true,
	maintainAspectRatio: false,
	plugins: {
		legend: {
			position: 'bottom' as const,
		},
	},
	scales: {
		y: {
			beginAtZero: true,
			ticks: {
				stepSize: 1,
			},
		},
	},
}

const recentActivities = computed(() => {
	const activities: Array<{
		type: 'material' | 'form' | 'profile'
		description: string
		details?: string
		time: string
		datetime: string
	}> = []

	completedForms.value.slice(0, 5).forEach(form => {
		const date = toDate(form.date_sent || form.created_at)
		const employeeName = form.form_from || (form.user?.details?.first_name && form.user?.details?.last_name
			? `${form.user.details.first_name} ${form.user.details.last_name}`
			: form.user?.email || 'Unknown')
		activities.push({
			type: 'form',
			description: `Completed form: ${form.form_subject || form.material?.title || 'Form'}`,
			details: `Employee: ${employeeName}`,
			time: formatTimeAgo(date),
			datetime: form.date_sent || form.created_at || '',
		})
	})

	materials.value.slice(0, 5).forEach(mat => {
		const date = new Date(mat.created_at)
		activities.push({
			type: 'material',
			description: `${mat.type} added: ${mat.title}`,
			time: formatTimeAgo(date),
			datetime: mat.created_at || '',
		})
	})

	return activities.sort((a, b) => new Date(b.datetime).getTime() - new Date(a.datetime).getTime()).slice(0, 10)
})

const toDate = (val: string | undefined): Date => new Date(val ?? '')

const formatTimeAgo = (date: Date): string => {
	const now = new Date()
	const diff = now.getTime() - date.getTime()
	const days = Math.floor(diff / (1000 * 60 * 60 * 24))
	const hours = Math.floor(diff / (1000 * 60 * 60))
	const minutes = Math.floor(diff / (1000 * 60))

	if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`
	if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`
	if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`
	return 'Just now'
}

const handleViewMaterials = () => {
  	router.push(isAdmin.value ? '/admin/materials' : '/employee-materials')
}

const handleViewCompletedForms = () => {
  	router.push('/admin/completed-forms')
}

const handleViewEmployees = () => {
  	router.push('/employees')
}

onMounted(async () => {
	loading.value = true
	chartLoading.value = true
	try {
		const [mats, forms] = await Promise.all([
			materialService.fetchAll({ per_page: 1000 }).catch(() => [] as any),
			completedFormService.fetchAll().catch(() => []),
		])
		materials.value = Array.isArray(mats) ? mats : (Array.isArray((mats as any)?.data) ? (mats as any).data : [])
		completedForms.value = forms

		if (isAdmin.value) {
			try {
				const { data } = await import('@/services/api').then(m => m.default.get('/users?per_page=1'))
				totalEmployees.value = data?.meta?.total || data?.total || 0
			} catch {
				totalEmployees.value = 0
			}
		}
	} catch (error) {
		console.error('Failed to load dashboard data:', error)
	} finally {
		loading.value = false
		chartLoading.value = false
	}
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, _toDisplayString(userInitials.value), 1),
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("h1", _hoisted_7, " Welcome back, " + _toDisplayString(userFullName.value) + "! ", 1),
            _createElementVNode("p", _hoisted_8, _toDisplayString(_unref(currentUser)?.email), 1)
          ])
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_9, [
      (isAdmin.value)
        ? (_openBlock(), _createBlock(DashboardStatsCard, {
            key: 0,
            title: "Total Employees",
            value: totalEmployees.value,
            loading: loading.value,
            icon: _unref(Users),
            color: "primary"
          }, null, 8, ["value", "loading", "icon"]))
        : _createCommentVNode("", true),
      _createVNode(DashboardStatsCard, {
        title: "Completed Forms",
        value: completedForms.value.length,
        loading: loading.value,
        icon: _unref(FileText),
        color: "success"
      }, null, 8, ["value", "loading", "icon"]),
      _createVNode(DashboardStatsCard, {
        title: "Materials",
        value: assignedMaterials.value.length,
        loading: loading.value,
        icon: _unref(File),
        color: "warning"
      }, null, 8, ["value", "loading", "icon"]),
      (!isAdmin.value)
        ? (_openBlock(), _createBlock(DashboardStatsCard, {
            key: 1,
            title: "Pending Forms",
            value: pendingForms.value,
            loading: loading.value,
            icon: _unref(Clock),
            color: "info"
          }, null, 8, ["value", "loading", "icon"]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_10, [
      _createVNode(QuickActionButton, {
        title: "View Materials",
        description: "Access learning resources",
        icon: _unref(File),
        color: "info",
        onClick: handleViewMaterials
      }, null, 8, ["icon"]),
      (isAdmin.value)
        ? (_openBlock(), _createBlock(QuickActionButton, {
            key: 0,
            title: "Completed Forms",
            description: "Review submitted forms",
            icon: _unref(FileText),
            color: "success",
            onClick: handleViewCompletedForms
          }, null, 8, ["icon"]))
        : _createCommentVNode("", true),
      (isAdmin.value)
        ? (_openBlock(), _createBlock(QuickActionButton, {
            key: 1,
            title: "View Employees",
            description: "Manage employee accounts",
            icon: _unref(Users),
            color: "primary",
            onClick: handleViewEmployees
          }, null, 8, ["icon"]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_11, [
      _cache[0] || (_cache[0] = _createElementVNode("div", { class: "card-header" }, [
        _createElementVNode("h2", { class: "text-lg sm:text-xl font-bold text-primary-text dark:text-primary-dark-text" }, " Monthly Analytics ")
      ], -1)),
      _createElementVNode("div", _hoisted_12, [
        (chartLoading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
              _createVNode(_unref(Loader2), { class: "h-8 w-8 animate-spin text-primary" })
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_14, [
              _createVNode(_unref(Bar), {
                data: chartData.value,
                options: chartOptions
              }, null, 8, ["data"])
            ]))
      ])
    ]),
    _createElementVNode("div", _hoisted_15, [
      _cache[1] || (_cache[1] = _createElementVNode("div", { class: "card-header" }, [
        _createElementVNode("h2", { class: "text-lg sm:text-xl font-bold text-primary-text dark:text-primary-dark-text" }, " Recent Activity ")
      ], -1)),
      _createElementVNode("div", _hoisted_16, [
        _createVNode(ActivityTimeline, { activities: recentActivities.value }, null, 8, ["activities"])
      ])
    ])
  ]))
}
}

})